import { AreaChart, MixBar } from "@/components/analytics/Charts";
import { TokenAmount, TokenLabel } from "@/components/brand/TokenAmount";
import { TokenIcon } from "@/components/brand/TokenIcon";
import { AnimatedNumber } from "@/components/defi/AnimatedNumber";
import { MetricsSourceBadge } from "@/components/ui/MetricsSourceBadge";
import { useCertifiedAnalytics } from "@/hooks/useCertifiedAnalytics";
import { useTokenomicsStatus } from "@/hooks/useGovernance";
import { useBadDebtSummary, useTreasuryOverview } from "@/hooks/useTreasury";
import { ROUTES } from "@/lib/routes";
import { Link } from "react-router-dom";
import { mmkProgressPct } from "@/lib/mmk-curve";
import {
	useGetPublicAnalyticsHistory,
	useGetPublicAnalyticsSummary,
	useGetPublicCollateralDistribution,
	useGetPublicCollateralParams,
	useProtocolRiskParams,
} from "@/hooks/usePublicProtocol";
import { cn, formatNum } from "@/lib/utils";
import { useMemo, useState } from "react";

const MIX_COLORS: Record<string, string> = {
	ICP: "#00c853",
	ckBTC: "#f7931a",
	ckETH: "#627eea",
};

type RangeKey = "1D" | "1W" | "1M" | "3M" | "6M" | "1Y" | "ALL";

const RANGES: { id: RangeKey; label: string; points: number | null }[] = [
	{ id: "1D", label: "1D", points: 1 },
	{ id: "1W", label: "1W", points: 7 },
	{ id: "1M", label: "1M", points: 30 },
	{ id: "3M", label: "3M", points: 90 },
	{ id: "6M", label: "6M", points: 180 },
	{ id: "1Y", label: "1Y", points: 365 },
	{ id: "ALL", label: "All", points: null },
];

function sliceHistory<T>(arr: T[], points: number | null): T[] {
	if (!arr.length) return arr;
	if (points == null || points >= arr.length) return arr;
	return arr.slice(-Math.max(1, points));
}

export default function AnalyticsPage() {
	const { data: certified, isLoading: certLoading } = useCertifiedAnalytics();
	const { data: live, isLoading: liveLoading } = useGetPublicAnalyticsSummary();
	const { data: mix = [] } = useGetPublicCollateralDistribution();
	const { data: history = [] } = useGetPublicAnalyticsHistory();
	const { data: params = [] } = useGetPublicCollateralParams();
	const { data: riskData } = useProtocolRiskParams();
	const { data: tokenomics } = useTokenomicsStatus();
	const { data: treasury } = useTreasuryOverview();
	const { data: badDebt } = useBadDebtSummary();
	const [range, setRange] = useState<RangeKey>("1M");

	/** Prefer certified; fall back to live query summary so metrics are not blank. */
	const stats = useMemo(() => {
		if (certified?.source === "certified") {
			return {
				tvlUsd: certified.tvlUsd,
				totalDebtUsd: certified.totalDebtUsd,
				activeVaults: certified.activeVaults,
				liquidatableCount: certified.liquidatableCount,
				atRiskCount: certified.atRiskCount,
				source: "certified" as const,
			};
		}
		if (certified?.source === "live") {
			return {
				tvlUsd: certified.tvlUsd,
				totalDebtUsd: certified.totalDebtUsd,
				activeVaults: certified.activeVaults,
				liquidatableCount: certified.liquidatableCount,
				atRiskCount: certified.atRiskCount,
				source: "live" as const,
			};
		}
		if (live?.source === "live") {
			return {
				tvlUsd: live.tvl,
				totalDebtUsd: live.totalDebt,
				activeVaults: live.activeVaults,
				liquidatableCount: live.liquidatableCount,
				atRiskCount: live.atRiskCount,
				source: "live" as const,
			};
		}
		if (certified?.source === "fixture" || live?.source === "fixture") {
			return {
				tvlUsd: certified?.tvlUsd ?? live?.tvl ?? 0,
				totalDebtUsd: certified?.totalDebtUsd ?? live?.totalDebt ?? 0,
				activeVaults: certified?.activeVaults ?? live?.activeVaults ?? 0,
				liquidatableCount:
					certified?.liquidatableCount ?? live?.liquidatableCount ?? 0,
				atRiskCount: certified?.atRiskCount ?? live?.atRiskCount ?? 0,
				source: "fixture" as const,
			};
		}
		return {
			tvlUsd: 0,
			totalDebtUsd: 0,
			activeVaults: 0,
			liquidatableCount: 0,
			atRiskCount: 0,
			source: "unverified" as const,
		};
	}, [certified, live]);

	const isLoading = certLoading && liveLoading;
	const rangeMeta = RANGES.find((r) => r.id === range) ?? RANGES[2]!;
	const hist = useMemo(
		() => sliceHistory(history, rangeMeta.points),
		[history, rangeMeta.points],
	);

	const apr =
		params[0] != null ? Number(params[0].stabilityFeeBps ?? 0) / 100 : null;

	const tvlSeries = hist.map((h: { tvl: number }) => h.tvl);
	const debtSeries = hist.map((h: { totalDebt: number }) => h.totalDebt);
	const historyLabels = hist.map((h: { label?: string }) => h.label ?? "");
	const showDashes = stats.source === "unverified";
	const mixSlices = mix.map((m) => ({
		asset: m.asset,
		sharePct: m.sharePct,
		color: MIX_COLORS[m.asset] ?? "#718096",
	}));

	const minCr = riskData?.minCollateralRatio ?? 1.5;
	const liqT = riskData?.liquidationThreshold ?? 1.3;
	const bonusBps = riskData?.liquidationBonusBps ?? 500;

	return (
		<div className="container mx-auto space-y-8 px-4 py-6 sm:py-8">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<p className="section-eyebrow">Analytics</p>
					<h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
						Protocol dashboard
					</h1>
					<p className="mt-1 max-w-xl text-sm text-muted-foreground">
						Key metrics from live protocol queries. Verified means an IC-certified
						digest — not a manual admin gate.
					</p>
				</div>
				<MetricsSourceBadge source={stats.source} />
			</div>

			{(stats.atRiskCount || stats.liquidatableCount) ? (
				<div className="rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">
					<strong>{stats.atRiskCount}</strong> vaults at risk ·{" "}
					<strong>{stats.liquidatableCount}</strong> liquidatable
				</div>
			) : null}

			{badDebt && badDebt.openBadDebtUsdm > 0 ? (
				<div className="rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm">
					<span className="font-semibold text-danger">Open bad debt </span>
					<span className="font-mono-amount text-danger">
						{formatNum(badDebt.openBadDebtUsdm, 2)} USDM
					</span>
					<span className="text-muted-foreground">
						{" "}
						· settled only from surplus (senior to waterfall) ·{" "}
					</span>
					<Link to={ROUTES.treasury} className="font-semibold text-primary underline">
						Treasury
					</Link>
				</div>
			) : null}

			{isLoading ? (
				<div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
					{Array.from({ length: 6 }).map((_, i) => (
						<div key={i} className="skeleton-shimmer h-24" />
					))}
				</div>
			) : (
				<div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
					{[
						[
							"TVL",
							showDashes ? "—" : `$${formatNum(stats.tvlUsd, 0)}`,
						],
						["Vaults", String(stats.activeVaults)],
						["At risk", String(stats.atRiskCount)],
						["Liquidatable", String(stats.liquidatableCount)],
						[
							"Stability fee",
							apr != null ? `${formatNum(apr, 1)}%` : "—",
						],
					].map(([lab, val], i) => (
						<div key={lab} className="card-surface p-4">
							<p className="text-xs uppercase text-muted-foreground">{lab}</p>
							<p
								className={cn(
									"mt-2 font-mono-amount text-xl font-bold sm:text-2xl",
									lab === "At risk" && "text-warning",
									lab === "Liquidatable" && "text-danger",
								)}
							>
								{i === 1 ? (
									<AnimatedNumber
										value={stats.activeVaults}
										decimals={0}
									/>
								) : (
									val
								)}
							</p>
						</div>
					))}
					<div className="card-surface p-4">
						<p className="text-xs uppercase text-muted-foreground">USDM debt</p>
						<div className="mt-2">
							{showDashes ? (
								"—"
							) : (
								<TokenAmount
									id="USDM"
									value={stats.totalDebtUsd}
									decimals={0}
									size="md"
								/>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Global parameters + collateral types */}
			<div className="grid gap-4 lg:grid-cols-2">
				<section className="card-surface space-y-3 p-5">
					<p className="section-eyebrow">Global parameters</p>
					{(riskData?.globalPaused ||
						riskData?.mintPaused ||
						riskData?.liquidatePaused) && (
						<div className="flex flex-wrap gap-1.5">
							{riskData.globalPaused ? (
								<span className="rounded-full bg-danger/15 px-2 py-0.5 text-[10px] font-bold text-danger">
									Global pause
								</span>
							) : null}
							{riskData.mintPaused ? (
								<span className="rounded-full bg-danger/15 px-2 py-0.5 text-[10px] font-bold text-danger">
									Mint paused
								</span>
							) : null}
							{riskData.liquidatePaused ? (
								<span className="rounded-full bg-danger/15 px-2 py-0.5 text-[10px] font-bold text-danger">
									Liq paused
								</span>
							) : null}
						</div>
					)}
					<ul className="divide-y divide-border/50 text-sm">
						{(
							[
								["Min collateral ratio", `${minCr.toFixed(2)}×`],
								["Liquidation threshold", `${liqT.toFixed(2)}×`],
								["Liquidation bonus", `${(bonusBps / 100).toFixed(1)}%`],
								[
									"Savings rate",
									`${((riskData?.savingsRateBps ?? 100) / 100).toFixed(2)}% APR`,
								],
								[
									"Global debt ceiling",
									riskData?.globalDebtCeiling
										? `$${formatNum(riskData.globalDebtCeiling, 0)}`
										: "—",
								],
								[
									"Total debt",
									riskData?.totalDebt != null
										? `$${formatNum(riskData.totalDebt, 0)}`
										: "—",
								],
							] as const
						).map(([lab, val]) => (
							<li key={lab} className="flex justify-between gap-3 py-2.5">
								<span className="text-muted-foreground">{lab}</span>
								<span className="font-mono-amount font-semibold">{val}</span>
							</li>
						))}
						<li className="flex justify-between gap-3 py-2.5">
							<span className="text-muted-foreground">Source</span>
							<span className="text-xs font-semibold text-primary">
								{riskData?.source === "live" ? "Live protocol" : "Fallback"}
							</span>
						</li>
					</ul>
				</section>

				<section className="card-surface overflow-x-auto p-5">
					<p className="section-eyebrow mb-3">Collateral types</p>
					{params.length === 0 ? (
						<p className="text-sm text-muted-foreground">No params loaded.</p>
					) : (
						<table className="w-full text-left text-sm">
							<thead>
								<tr className="border-b border-border text-xs text-muted-foreground">
									<th className="py-2 font-medium">Asset</th>
									<th className="py-2 font-medium">Max LTV</th>
									<th className="py-2 font-medium">Liq. thr.</th>
									<th className="py-2 font-medium">Fee bps</th>
								</tr>
							</thead>
							<tbody>
								{params.map(
									(p: {
										asset: string;
										maxLtv: number;
										liqThreshold: number;
										stabilityFeeBps: number;
									}) => (
										<tr key={p.asset} className="border-b border-border/40">
											<td className="py-2.5">
												<TokenLabel id={p.asset} size="sm" />
											</td>
											<td className="py-2.5 font-mono-amount">
												{(p.maxLtv <= 1 ? p.maxLtv * 100 : p.maxLtv).toFixed(0)}
												%
											</td>
											<td className="py-2.5 font-mono-amount">
												{(p.liqThreshold <= 1
													? p.liqThreshold * 100
													: p.liqThreshold
												).toFixed(0)}
												%
											</td>
											<td className="py-2.5 font-mono-amount">
												{p.stabilityFeeBps}
											</td>
										</tr>
									),
								)}
							</tbody>
						</table>
					)}
				</section>
			</div>

			<section className="card-surface space-y-4 p-5">
				<p className="section-eyebrow">Collateral mix</p>
				{mixSlices.length > 0 ? (
					<>
						<MixBar slices={mixSlices} />
						<ul className="mt-3 space-y-2">
							{mix.map((m) => (
								<li
									key={m.asset}
									className="flex items-center justify-between gap-3"
								>
									<span className="inline-flex items-center gap-2">
										<TokenIcon token={m.asset} size="sm" />
										<span className="text-sm font-semibold">
											{m.asset}{" "}
											<span className="font-mono font-normal text-muted-foreground">
												({m.sharePct.toFixed(0)}%)
											</span>
										</span>
									</span>
									<span className="font-mono-amount text-sm font-bold sm:text-base">
										${formatNum(m.lockedUsd, 0)} locked
									</span>
								</li>
							))}
						</ul>
					</>
				) : (
					<p className="text-sm text-muted-foreground">No mix data</p>
				)}
			</section>

			{/* Range filter */}
			<div className="flex flex-wrap gap-1.5">
				{RANGES.map((r) => (
					<button
						key={r.id}
						type="button"
						onClick={() => setRange(r.id)}
						className={cn(
							"min-h-9 rounded-full border px-3 text-xs font-semibold",
							range === r.id
								? "border-primary bg-primary/15 text-primary"
								: "border-border text-muted-foreground",
						)}
					>
						{r.label}
					</button>
				))}
			</div>

			<section className="grid gap-4 sm:grid-cols-2">
				<div className="card-surface p-5">
					<p className="section-eyebrow">TVL trend</p>
					<p className="mt-1 text-xs text-muted-foreground">
						Range: {rangeMeta.label}
						{hist.length ? ` · ${hist.length} points` : ""}
					</p>
					<div className="mt-3">
						{hist.length < 1 ? (
							<p className="py-8 text-center text-sm text-muted-foreground">
								Snapshots accumulate hourly from live analytics. Open this page
								again later or wait for the protocol timer.
							</p>
						) : (
							<AreaChart values={tvlSeries} labels={historyLabels} />
						)}
					</div>
				</div>
				<div className="card-surface p-5">
					<p className="section-eyebrow">Debt trend</p>
					<p className="mt-1 text-xs text-muted-foreground">USDM total debt</p>
					<div className="mt-3">
						{hist.length < 1 ? (
							<p className="py-8 text-center text-sm text-muted-foreground">
								No history points yet — snapshots record about once per hour.
							</p>
						) : (
							<AreaChart
								values={debtSeries}
								labels={historyLabels}
								colorClass="text-warning"
							/>
						)}
					</div>
				</div>
			</section>

			<section className="card-surface space-y-4 p-5">
				<div className="flex items-center gap-2">
					<TokenIcon token="USDM" size="sm" />
					<p className="section-eyebrow !mt-0">USDM distribution</p>
				</div>
				{(() => {
					const debt = !showDashes ? stats.totalDebtUsd : 0;
					const sav = treasury?.savingsDepositedUsdm ?? 0;
					const sur = treasury?.surplusUsdm ?? 0;
					const sum = debt + sav + sur || 1;
					const slices = [
						{
							asset: "Vault debt",
							sharePct: Math.round((debt / sum) * 100),
							color: "#00c853",
						},
						{
							asset: "Savings pool",
							sharePct: Math.round((sav / sum) * 100),
							color: "#69f0ae",
						},
						{
							asset: "Surplus",
							sharePct: Math.round((sur / sum) * 100),
							color: "#a3e635",
						},
					].filter((s) => s.sharePct > 0);
					return slices.length ? (
						<>
							<MixBar slices={slices} />
							<ul className="mt-2 space-y-1 font-mono text-sm text-muted-foreground">
								<li className="flex justify-between gap-3">
									<span>
										Vault debt (
										{Math.round((debt / sum) * 100)}%)
									</span>
									<span>${formatNum(debt, 0)}</span>
								</li>
								<li className="flex justify-between gap-3">
									<span>
										Savings pool (
										{Math.round((sav / sum) * 100)}%)
									</span>
									<span>${formatNum(sav, 0)}</span>
								</li>
								<li className="flex justify-between gap-3">
									<span>
										Surplus (
										{Math.round((sur / sum) * 100)}%)
									</span>
									<span>${formatNum(sur, 0)}</span>
								</li>
							</ul>
						</>
					) : (
						<p className="text-sm text-muted-foreground">
							No distribution data yet.
						</p>
					);
				})()}
			</section>

			<section className="card-surface space-y-4 p-5">
				<div className="flex items-center gap-2">
					<TokenIcon token="MMK" size="md" />
					<div>
						<p className="section-eyebrow !mt-0">Governance</p>
						<h2 className="text-base font-bold sm:text-lg">
							Governance MMK token distribution status
						</h2>
					</div>
				</div>
				{tokenomics ? (
					<>
						<div className="grid gap-3 sm:grid-cols-3">
							<div>
								<p className="text-xs uppercase text-muted-foreground">Sold</p>
								<p className="font-mono-amount text-lg font-bold sm:text-xl">
									{formatNum(tokenomics.soldMmk, 0)}
								</p>
							</div>
							<div>
								<p className="text-xs uppercase text-muted-foreground">Cap</p>
								<p className="font-mono-amount text-lg font-bold sm:text-xl">
									{formatNum(tokenomics.capMmk, 0)}
								</p>
							</div>
							<div>
								<p className="text-xs uppercase text-muted-foreground">Spot</p>
								<p className="font-mono-amount text-lg font-bold sm:text-xl">
									{formatNum(tokenomics.spotUsdmPerMmk, 4)} USDM
								</p>
							</div>
						</div>
						<div className="h-3 w-full overflow-hidden rounded-full bg-muted">
							<div
								className="h-full rounded-full bg-primary"
								style={{
									width: `${mmkProgressPct(tokenomics.soldMmk, tokenomics.capMmk)}%`,
								}}
							/>
						</div>
						<p className="text-sm text-muted-foreground">
							{mmkProgressPct(tokenomics.soldMmk, tokenomics.capMmk).toFixed(1)}%
							of cap sold
						</p>
					</>
				) : (
					<p className="text-sm text-muted-foreground">
						Tokenomics unavailable offline.
					</p>
				)}
			</section>
		</div>
	);
}
